import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { getFriendSuggestions } from "../../functions/user";
import Card from "./Card";
import "./style.css";

export default function Suggestions() {
  const { user } = useSelector((state) => ({ ...state }));
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Utility function to shuffle array (Fisher-Yates shuffle algorithm)
  const shuffleArray = (array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  const getSuggestions = useCallback(async (shouldShuffle = false) => {
    try {
      setLoading(true);
      const response = await getFriendSuggestions(user.token);
      if (response.status === "ok") {
        const suggestionsData = response.data.suggestions;
        // If shouldShuffle is true, shuffle the suggestions
        const finalSuggestions = shouldShuffle ? shuffleArray(suggestionsData) : suggestionsData;
        setSuggestions(finalSuggestions);
        setError("");
      } else {
        setError(response);
      }
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setError("Failed to load suggestions");
    } finally {
      setLoading(false);
    }
  }, [user.token]);

  useEffect(() => {
    getSuggestions();
  }, [getSuggestions]);

  const refreshSuggestions = async () => {
    setIsRefreshing(true);
    await getSuggestions(true); // Pass true to shuffle the results
    // Add a small delay to show the animation
    setTimeout(() => {
      setIsRefreshing(false);
    }, 500);
  };

  if (loading) {
    return (
      <div className="suggestions_container">
        <div className="friends_left_header">
          <h3>People You May Know</h3>
        </div>
        <div className="loading">Loading suggestions...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="suggestions_container">
        <div className="friends_left_header">
          <h3>People You May Know</h3>
        </div>
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="suggestions_container">
      <div className="friends_left_header">
        <h3>People You May Know</h3>
        <div className="suggestions_count">
          {suggestions.length > 0 && `${suggestions.length} suggestions`}
        </div>
      </div>

      {suggestions.length === 0 ? (
        <div className="no_data">
          <div className="suggestion_icon">👥</div>
          <p>No suggestions available at the moment</p>
          <p className="sub_text">Check back later for new people to connect with!</p>
        </div>
      ) : (
        <div className="suggestions_grid">
          {suggestions.map((suggestion) => (
            <div key={suggestion._id} className="suggestion_card">
              <Link to={`/profile/${suggestion.username || suggestion._id}`} className="suggestion_link">
                <div className="suggestion_avatar">
                  <img 
                    src={suggestion.picture} 
                    alt={suggestion.name}
                    onError={(e) => {
                      e.target.src = "https://res.cloudinary.com/dmhcnhtng/image/upload/v1643044376/avatars/default_pic_jeaybr.png";
                    }}
                  />
                </div>
                <div className="suggestion_info">
                  <div className="suggestion_name">{suggestion.name}</div>
                  <div className="suggestion_username">@{suggestion.username}</div>
                </div>
              </Link>
              <div className="suggestion_actions">
                <Card
                  userr={suggestion}
                  type="suggestion"
                  getData={() => getSuggestions(false)}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {suggestions.length > 0 && (
        <div className="suggestions_footer">
          <button 
            onClick={refreshSuggestions} 
            className="refresh_suggestions"
            disabled={isRefreshing}
          >
            <span className={isRefreshing ? "spinning" : ""}>🔄</span> 
            {isRefreshing ? "Shuffling..." : "Refresh Suggestions"}
          </button>
        </div>
      )}
    </div>
  );
} 